import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AuthenticationContext } from "components/context/AuthenticationProvider";
import { useContext, useEffect, useState } from "react";
import { SnackbarContext } from "components/context/SnackbarProvider";

export const baseUrl = "/api";

export const retryOnError = (count, error) => {
  if (
    error?.response?.status !== undefined &&
    error?.response?.status < 500 &&
    error?.response?.status >= 400
  ) {
    error.message = error.response?.data?.detail;
    //console.log(JSON.stringify(error));
    return false;
  } else {
    return count < 3;
  }
};

export const sendCacheDiscardEvent = (key, type) => {
  if (!Array.isArray(key)) throw new Error("Key must be an array");

  const event = new CustomEvent("cacheDiscard", {
    detail: { key, type },
  });
  window.dispatchEvent(event);
};

export const useCacheDiscardEventListener = (key, callback) => {
  if (!Array.isArray(key)) throw new Error("Key must be an array");

  const handleCacheEvent = (event) => {
    if (key.length > event.detail.key.length) {
      return false;
    }

    if (key.every((value, index) => value === event.detail.key[index])) {
      callback(event.detail);
    }
  };

  useEffect(() => {
    window.addEventListener("cacheDiscard", handleCacheEvent);
    return () => {
      window.removeEventListener("cacheDiscard", handleCacheEvent);
    };
  }, [key, callback]);

  return "listener added";
};

export const useObjectDeleted = (key) => {
  const [deleted, setDeleted] = useState(false);

  useCacheDiscardEventListener(key, (detail) => {
    if (detail.type === "deleteOne") {
      setDeleted(true);
    }
  });

  useEffect(() => {
    setDeleted(false);
  }, [JSON.stringify(key)]);

  return deleted;
};

export const apiCrudBuilder = (path, keyName, queryOptions) => {
  const useGetAll = (params, options) => {
    const { appAxios } = useContext(AuthenticationContext);
    const queryClient = useQueryClient();

    return useQuery({
      ...queryOptions,
      ...options,
      queryKey: [`all_${keyName}`, params],
      queryFn: () => {
        return appAxios.get(`${path}/`, { params: params }).then((res) => {
          // results exist only if there is pagination
          (res.data.results || res.data).forEach((item) => {
            queryClient.setQueryData([keyName, `${item.id}`], item, {
              staleTime: 10000,
              cacheTime: 10000,
            });
          });

          return res.data;
        });
      },
      enabled: params !== null,
      retry: retryOnError,
    });
  };

  const useGet = (id, options) => {
    const { appAxios } = useContext(AuthenticationContext);

    return useQuery({
      staleTime: 10000,
      cacheTime: 10000,
      ...queryOptions,
      ...options,
      queryKey: [keyName, `${id}`],
      queryFn: () => appAxios.get(`${path}/${id}/`).then((res) => res.data),
      retry: retryOnError,
    });
  };

  const useGetAsync = (options) => {
    const { appAxios } = useContext(AuthenticationContext);
    const queryClient = useQueryClient();

    return (id) =>
      queryClient.fetchQuery({
        queryKey: [keyName, `${id}`],
        staleTime: 10000,
        cacheTime: 10000,
        ...options,
        queryFn: () => appAxios.get(`${path}/${id}/`).then((res) => res.data),
      });
  };

  const useCreate = (options) => {
    const queryClient = useQueryClient();
    const { appAxios } = useContext(AuthenticationContext);
    const { displayError } = useContext(SnackbarContext);

    return useMutation({
      ...options,
      mutationFn: (newObject) =>
        appAxios.post(`${path}/`, newObject).then((res) => res.data),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [`all_${keyName}`] });
      },
      onError: displayError,
    });
  };

  const useUpdate = (id, options) => {
    const queryClient = useQueryClient();
    const { appAxios } = useContext(AuthenticationContext);
    const { displayError } = useContext(SnackbarContext);

    return useMutation({
      ...options,
      mutationFn: (updates) =>
        appAxios.put(`${path}/${id}/`, updates).then((res) => res.data),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [keyName, `${id}`] });
        queryClient.invalidateQueries({ queryKey: [`all_${keyName}`] });
      },
      onError: displayError,
    });
  };

  const useDelete = (id, options) => {
    const queryClient = useQueryClient();
    const { appAxios } = useContext(AuthenticationContext);
    const { displayError } = useContext(SnackbarContext);

    return useMutation({
      ...options,
      mutationFn: () =>
        appAxios.delete(`${path}/${id}/`).then((res) => res.data),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [keyName, id] });
        queryClient.invalidateQueries({ queryKey: [`all_${keyName}`] });
      },
      onError: displayError,
    });
  };

  return {
    useGetAll,
    useGet,
    useGetAsync,
    useCreate,
    useUpdate,
    useDelete,
  };
};

export default apiCrudBuilder;
